import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import './PricingPage.css';
import logo from '../../images/logo_white.png';

const LightningIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true">
    <path d="M13 2L4.5 13.5H11L9 22l9.5-11.5H12.5L13 2Z" />
  </svg>
);

const ReportIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M8 7h8M8 11h8M8 15h5" />
  </svg>
);

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
    <path d="M3 20V14M8 20V10M13 20V6M18 20V3" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ICON_MAP = {
  lightning: <LightningIcon />,
  report:    <ReportIcon />,
  chart:     <ChartIcon />,
  check:     <CheckIcon />,
  users:     <UsersIcon />,
};

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'For exploring reports',
    price: 'Free',
    priceSub: 'Forever free tier',
    cta: 'Get Started',
    ctaHref: '/sign-up',
    ctaVariant: 'outline',
    features: [
      { icon: 'lightning', text: '50 credits / month' },
      { icon: 'report',    text: 'Up to 4 report types' },
      { icon: 'chart',     text: 'Basic analytics' },
      { icon: 'users',     text: '1 user account' },
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    tagline: 'For growing teams',
    price: '$99',
    pricePeriod: '/mo',
    priceSub: '2,000 credits included',
    recommended: true,
    cta: 'Start Free Trial',
    ctaHref: '/sign-up',
    ctaVariant: 'light',
    features: [
      { icon: 'lightning', text: '2,000 credits / month' },
      { icon: 'report',    text: 'All 4 report types' },
      { icon: 'chart',     text: 'Advanced analytics' },
      { icon: 'users',     text: 'Up to 3 users' },
      { icon: 'check',     text: 'Priority support' },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For large organisations',
    price: 'Custom',
    priceSub: 'Unlimited credits available',
    cta: 'Contact Sales',
    ctaHref: '/contact-us',
    ctaVariant: 'outline',
    features: [
      { icon: 'lightning', text: 'Custom credit limits' },
      { icon: 'report',    text: 'All report types + custom' },
      { icon: 'chart',     text: 'Custom integrations' },
      { icon: 'users',     text: 'Unlimited users' },
      { icon: 'check',     text: 'Dedicated support' },
    ],
  },
];

const faqs = [
  {
    q: 'Can I buy additional credits?',
    a: 'Yes! You can purchase additional credits at $0.50 per credit anytime during your billing cycle.',
  },
  {
    q: 'Do credits expire?',
    a: 'Monthly credits roll over, but we recommend using them within 12 months for optimal account management.',
  },
  {
    q: 'Can I change plans anytime?',
    a: 'Upgrade or downgrade your plan anytime. Changes take effect on your next billing cycle.',
  },
  {
    q: 'Is there a contract?',
    a: 'Nope! All plans are month-to-month. Cancel anytime with no penalties or hidden fees.',
  },
];

const PricingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pricing-container">

      {/* ── Navbar ── */}
      <nav className="pricing-navbar">
        <div className="pricing-nav-content">
          <div className="pricing-logo" onClick={() => (window.location.href = '/')}>
            <img src={logo} alt="TalentScope360" />
          </div>

          <button
            className="pricing-mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="pricing-nav-links pricing-nav-desktop">
            <a href="/#features"     className="pricing-nav-link">Features</a>
            <a href="/pricing"       className="pricing-nav-link pricing-nav-link--active">Pricing</a>
            <a href="/documentation" className="pricing-nav-link">Documentation</a>
          </div>

          <div className="pricing-nav-buttons pricing-nav-desktop">
            <a href="/login"  className="pricing-btn-login">Login</a>
            <a href="/sign-up" className="pricing-btn-getstarted">Get Started</a>
          </div>
        </div>

        <div className={`pricing-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <a href="/#features"     className="pricing-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>Features</a>
          <a href="/pricing"       className="pricing-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
          <a href="/documentation" className="pricing-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>Documentation</a>
          <div className="pricing-mobile-menu-buttons">
            <a href="/login"  className="pricing-btn-login">Login</a>
            <a href="/sign-up" className="pricing-btn-getstarted">Get Started</a>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="pricing-hero">
        <div className="pricing-hero-orb pricing-hero-orb--1" aria-hidden="true" />
        <div className="pricing-hero-orb pricing-hero-orb--2" aria-hidden="true" />
        <div className="pricing-hero-grid"                    aria-hidden="true" />
        <div className="pricing-hero-content">
          <div className="pricing-hero-badge">
            <span className="pricing-hero-badge-dot" aria-hidden="true" />
            Credit-Based Pricing
          </div>
          <h1 className="pricing-hero-title">
            Flexible Pricing for{' '}
            <span className="pricing-hero-title-accent">Your Report Needs</span>
          </h1>
          <p className="pricing-hero-subtitle">
            Get started free. Scale with our credit-based subscription plans.
            Pay only for what you generate.
          </p>
        </div>
      </section>

      {/* ── Pricing Cards ── */}
      <section className="pricing-plans-section" id="plans">
        <div className="pricing-plans-grid">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`pricing-card${plan.recommended ? ' pricing-card--recommended' : ''}`}
            >
              {plan.recommended && (
                <div className="pricing-card-badge">RECOMMENDED</div>
              )}

              <div className="pricing-card-header">
                <h3 className="pricing-card-name">{plan.name}</h3>
                <p className="pricing-card-tagline">{plan.tagline}</p>
              </div>

              <div className="pricing-card-price-row">
                <span className="pricing-card-amount">{plan.price}</span>
                {plan.pricePeriod && (
                  <span className="pricing-card-period">{plan.pricePeriod}</span>
                )}
              </div>
              <p className="pricing-card-price-sub">{plan.priceSub}</p>

              <div className="pricing-card-divider" aria-hidden="true" />

              <ul className="pricing-card-features">
                {plan.features.map((feat, i) => (
                  <li key={i} className="pricing-card-feature">
                    <span className="pricing-feature-icon">
                      {ICON_MAP[feat.icon]}
                    </span>
                    {feat.text}
                  </li>
                ))}
              </ul>

              <a
                href={plan.ctaHref}
                className={`pricing-card-cta pricing-card-cta--${plan.ctaVariant}`}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* ── How Credits Work ── */}
      <section className="pricing-credits-section">
        <div className="pricing-credits-inner">
          <div className="pricing-credits-header">
            <h2 className="pricing-credits-title">How Credits Work</h2>
            <p className="pricing-credits-subtitle">
              Every report type costs 250 credits. Credits are included in your plan monthly and unused credits roll over.
            </p>
          </div>
          <div className="pricing-credits-grid">
            <div className="pricing-credits-col">
              <div className="pricing-credits-col-icon" aria-hidden="true">
                <ReportIcon />
              </div>
              <h4 className="pricing-credits-col-title">Report Types &amp; Cost</h4>
              <ul className="pricing-credits-list">
                <li><span className="pricing-credits-dot" aria-hidden="true" />Corporate Reports — <strong>250 credits each</strong></li>
                <li><span className="pricing-credits-dot" aria-hidden="true" />Functional Reports — <strong>250 credits each</strong></li>
                <li><span className="pricing-credits-dot" aria-hidden="true" />Individual Reports — <strong>250 credits each</strong></li>
                <li><span className="pricing-credits-dot" aria-hidden="true" />Admin Reports — <strong>250 credits each</strong></li>
              </ul>
            </div>
            <div className="pricing-credits-divider" aria-hidden="true" />
            <div className="pricing-credits-col">
              <div className="pricing-credits-col-icon" aria-hidden="true">
                <LightningIcon />
              </div>
              <h4 className="pricing-credits-col-title">Monthly Inclusions</h4>
              <ul className="pricing-credits-list">
                <li><span className="pricing-credits-dot" aria-hidden="true" /><strong>Starter:</strong> 50 credits (perfect for testing)</li>
                <li><span className="pricing-credits-dot" aria-hidden="true" /><strong>Professional:</strong> 2,000 credits (8 reports/month)</li>
                <li><span className="pricing-credits-dot" aria-hidden="true" /><strong>Enterprise:</strong> Custom limits</li>
                <li><span className="pricing-credits-dot" aria-hidden="true" />Unused credits roll over each month</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="pricing-faq-section">
        <div className="pricing-faq-header">
          <h2 className="pricing-faq-title">Frequently Asked Questions</h2>
          <p className="pricing-faq-subtitle">Everything you need to know about credits and billing.</p>
        </div>
        <div className="pricing-faq-grid">
          {faqs.map((faq, i) => (
            <div key={i} className="pricing-faq-card">
              <h4 className="pricing-faq-q">{faq.q}</h4>
              <p className="pricing-faq-a">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA strip ── */}
      <section className="pricing-cta-strip">
        <div className="pricing-cta-strip-inner">
          <p className="pricing-cta-strip-text">
            Not sure which plan is right for you?
          </p>
          <a href="/contact-us" className="pricing-cta-strip-btn">
            Talk to our team
          </a>
        </div>
      </section>

      {/* ── Footer note ── */}
      <div className="pricing-footer-note">
        <div className="pricing-legal-links">
          <a href="/terms-of-service" className="pricing-link">Terms of Service</a>
          <span className="pricing-divider" aria-hidden="true">·</span>
          <a href="/privacy-policy" className="pricing-link">Privacy Policy</a>
          <span className="pricing-divider" aria-hidden="true">·</span>
          <a href="/contact-us" className="pricing-link">Contact Us</a>
        </div>
      </div>

      {/* ── Page Footer ── */}
      <footer className="pricing-footer-bottom">
        <p>© {currentYear} TalentScope360. All rights reserved.</p>
      </footer>

    </div>
  );
};

export default PricingPage;
